import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilePdfIcon } from '@phosphor-icons/react';
import { Button, Dialog, EmptyState } from '@origo/origo-ui';
import PlanDetailHeader from '../../components/PlanDetailHeader';
import DocumentList, { type DocumentAction } from './components/DocumentList';
import NewDocumentDialog from './components/NewDocumentDialog';
import type { DocumentFieldErrors } from './components/DocumentForm';
import type { DocumentFormValues, DocumentItem } from '../../types';
import styles from './DocumentsPage.module.css';

export interface NewDocumentState {
    open: boolean;
    values: DocumentFormValues;
    file: File | null;
    fieldErrors: DocumentFieldErrors;
    fileError?: string;
    isSaving: boolean;
}

interface DocumentsPageProps {
    items: DocumentItem[];
    onOpen: (id: string) => void;

    newDocument: NewDocumentState;
    onNewOpen: () => void;
    onNewClose: () => void;
    onNewChange: (field: keyof DocumentFormValues, value: string) => void;
    onNewFileChange: (file: File | null) => void;
    onNewSave: () => void;

    onDeleteRequest: (id: string) => void;
    pendingDelete: DocumentItem | null;
    onDeleteConfirm: () => void;
    onDeleteCancel: () => void;
    isDeleting?: boolean;
}

const DocumentsPage: React.FC<DocumentsPageProps> = ({
    items,
    onOpen,
    newDocument,
    onNewOpen,
    onNewClose,
    onNewChange,
    onNewFileChange,
    onNewSave,
    onDeleteRequest,
    pendingDelete,
    onDeleteConfirm,
    onDeleteCancel,
    isDeleting,
}) => {
    const { t } = useTranslation();
    const [replaceStubOpen, setReplaceStubOpen] = useState(false);

    const isEmpty = items.length === 0;

    const handleAction = (action: DocumentAction, id: string) => {
        if (action === 'replace') setReplaceStubOpen(true);
        else onDeleteRequest(id);
    };

    return (
        <article className={styles.page}>
            <PlanDetailHeader
                title={t('documents.title')}
                subtitle={t('documents.subtitle')}
                actions={
                    !isEmpty ? (
                        <Button variant="secondary" size="md" onClick={onNewOpen}>
                            {t('documents.newDocument')}
                        </Button>
                    ) : undefined
                }
            />
            <div className={styles.body}>
                {isEmpty ? (
                    <EmptyState
                        icon={<FilePdfIcon size={32} weight="duotone" />}
                        title={t('documents.empty.title')}
                        description={t('documents.empty.description')}
                        action={
                            <Button variant="primary" size="lg" onClick={onNewOpen}>
                                {t('documents.empty.action')}
                            </Button>
                        }
                    />
                ) : (
                    <DocumentList
                        items={items}
                        onOpen={onOpen}
                        onAction={handleAction}
                    />
                )}
            </div>
            <NewDocumentDialog
                open={newDocument.open}
                values={newDocument.values}
                file={newDocument.file}
                fieldErrors={newDocument.fieldErrors}
                fileError={newDocument.fileError}
                onChange={onNewChange}
                onFileChange={onNewFileChange}
                onClose={onNewClose}
                onSave={onNewSave}
                isSaving={newDocument.isSaving}
            />
            <Dialog
                open={replaceStubOpen}
                onClose={() => setReplaceStubOpen(false)}
                title={t('documents.replaceStub.title')}
                footer={
                    <Button variant="primary" onClick={() => setReplaceStubOpen(false)}>
                        {t('documents.replaceStub.close')}
                    </Button>
                }
            >
                <p>{t('documents.replaceStub.body')}</p>
            </Dialog>
            <Dialog
                open={pendingDelete !== null}
                onClose={onDeleteCancel}
                title={t('documents.delete.title')}
                footer={
                    <div className={styles.dialogFooter}>
                        <Button variant="secondary" onClick={onDeleteCancel} disabled={isDeleting}>
                            {t('common.cancel')}
                        </Button>
                        <Button variant="danger" onClick={onDeleteConfirm} disabled={isDeleting}>
                            {t('documents.delete.confirm')}
                        </Button>
                    </div>
                }
            >
                <p>{t('documents.delete.body', { navn: pendingDelete?.dokumentnavn ?? '' })}</p>
            </Dialog>
        </article>
    );
};

export default DocumentsPage;
